import './DispatchBoard.scss';
import { Alert, UncontrolledTooltip, Button } from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHardHat, faInfoCircle, faRecycle } from '@fortawesome/free-solid-svg-icons';
import { crewLeadLabel, weekdayShort } from '@s20/i18n';
import { appendTimeZone } from '@s20/common/util/date';
import { getTimezones } from '@s20/common/util/l10n';
import {
    ActivityIcons,
} from '../Common';

const jobTime = (assignmentDate, cancelledOn, jobTimeZone, overrides) => {
    const { ianaTimezones } = getTimezones();

    const override = overrides.find(({ overrideDate }) =>
        moment(overrideDate).isSame(moment(assignmentDate), 'day'));

    const jTimeZone = override?.timeZone ? override.timeZone : jobTimeZone;
    const time = ianaTimezones?.find((tz) => tz.id === jTimeZone || tz.value === jTimeZone)?.value;
    const local = moment(cancelledOn).format('M/D/YY, h:mm A');
    const timeOffset = moment(cancelledOn).tz(time).format();
    return appendTimeZone(local, timeOffset, jTimeZone);
};

const getCancellationReasonText = (job, day, cancellationId) => {
    const jobCancellation = job.assignments[day.getDay()]?.cancelledJobs?.find(
        (jc) => jc.id === cancellationId,
    );

    return jobCancellation?.jobCancellationReasonType;
};

const DispatchCancellationJobAssignments = (props) => {
    const {
        job, selectedWeekRangeDays, tenantSettings, onViewJobWorkflowClicked,
    } = { ...props };
    const crewLead = crewLeadLabel(tenantSettings);

    return (
        <div className="jobAssignmentsTableContainer">
            <table className="table table-sm jobAssignmentsTable mb-0">
                <thead>
                    <tr>
                        {
                            (selectedWeekRangeDays ?? []).map((day) => (
                                <th key={day}>
                                    <div className="w-100 job-assignment-table-header d-flex flex-row flex-nowrap">
                                        <span className="text-left flex-fill">{weekdayShort(day.getDay())}</span>
                                        <span className="text-muted">{`${day.getMonth() + 1}/${day.getDate()}`}</span>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {
                            (selectedWeekRangeDays ?? []).map((day) => {
                                const hasCancels = !!(job.assignments[day.getDay()] ?? []).cancelledJobs.length
                                    || !!(job.assignments[day.getDay()] ?? []).cancelledAssignments.length;
                                return (
                                    <td key={day}>
                                        <div className="d-flex flex-wrap align-items-end">
                                            <Button
                                                color="link"
                                                size="sm"
                                                className="site-link text-danger mt-1 mb-1 d-inline"
                                                onClick={() => onViewJobWorkflowClicked(job, day.getDay())}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faRecycle}
                                                    className="mr-1"
                                                />
                                                View Job Workflow
                                            </Button>
                                        </div>
                                        <div className="assignmentsWrapper">
                                            <div className="assignments">
                                                {
                                                    !!hasCancels
                                                    && job.assignments[day.getDay()].cancelledAssignments.map((a) => {
                                                        const reasonText = a.jobCancellationId
                                                            ? getCancellationReasonText(job, day, a.jobCancellationId)
                                                            : null;
                                                        return (
                                                            <div
                                                                className="jobCancellationBadge"
                                                                key={a.id}
                                                            >
                                                                <div className="jobCancellationBadgeDetails">
                                                                    <div className="jobCancellationBadgeAsn">
                                                                        {
                                                                            !!a.isCrewLead
                                                                            && (
                                                                                <FontAwesomeIcon
                                                                                    icon={faHardHat}
                                                                                    className="mr-1 crewLeadBadge"
                                                                                    style={{ color: '#ff8d00' }}
                                                                                    title={`Employee was ${crewLead}`}
                                                                                />
                                                                            )
                                                                        }
                                                                        <span className="font-weight-bold text-danger mr-1">
                                                                            {
                                                                                (a.assignedEmployee ? a.assignedEmployee : a.assignedEquipment)
                                                                            }
                                                                        </span>
                                                                        <span className="font-weight-bold text-muted">
                                                                            {`- ${a.jobAssignmentCancellationType}`}
                                                                        </span>
                                                                        {reasonText && (
                                                                            <>
                                                                                <FontAwesomeIcon
                                                                                    id={`reasonType-${a.id}`}
                                                                                    icon={faInfoCircle}
                                                                                    size="md"
                                                                                    className="ml-1"
                                                                                />
                                                                                <UncontrolledTooltip
                                                                                    key={`tooltip-${a.id}`}
                                                                                    placement="top"
                                                                                    target={`reasonType-${a.id}`}
                                                                                    delay={{ show: 0, hide: 0 }}
                                                                                >
                                                                                    {reasonText}
                                                                                </UncontrolledTooltip>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <div className="jobCancellationBadgeAsnCancelType">
                                                                        <div className="mr-2">
                                                                            <span
                                                                                title="Cancelling User"
                                                                                className="fa fa-sm fa-user mr-1 text-danger"
                                                                            />
                                                                            <span>{`${a.cancelledByEmployee}`}</span>
                                                                        </div>
                                                                        <div className="mr-2">
                                                                            <span
                                                                                title="Cancellation Date and Time"
                                                                                className="fa fa-sm fa-calendar-times mr-1 text-danger"
                                                                            />
                                                                            <span>{jobTime(a.assignmentDate, a.cancelledOn,
                                                                                job.timeZone, job.overrides)}</span>
                                                                        </div>
                                                                        {!!a.lastEvent && (
                                                                            <span
                                                                                className="mr-2"
                                                                            >
                                                                                <FontAwesomeIcon
                                                                                    icon={ActivityIcons[a.lastEvent]}
                                                                                    className="mr-1 text-danger"
                                                                                    title="Employee's Last Event"
                                                                                />
                                                                                <span>{a.lastEvent}</span>
                                                                            </span>
                                                                        )}
                                                                        {
                                                                            !!a.note
                                                                            && (
                                                                                <div className="d-flex flex-row justify-content-start align-items-center mr-2">
                                                                                    <span
                                                                                        style={{ textAlign: 'left !important' }}
                                                                                        title="Cancellation note"
                                                                                        className="fa fa-lg fa-sticky-note mr-1 text-danger"
                                                                                    />
                                                                                    <small>{a.note}</small>
                                                                                </div>
                                                                            )
                                                                        }
                                                                        {
                                                                            !!a.confirmedOn
                                                                            && (
                                                                                <Alert
                                                                                    size="sm"
                                                                                    className="cancelConfirmAlert"
                                                                                    color="info"
                                                                                >
                                                                                    <span className="fa fa-lg fa-check-circle mr-1" />
                                                                                    {`Confirmed - ${jobTime(a.assignmentDate, a.confirmedOn,
                                                                                        job.timeZone, job.overrides)}`}
                                                                                </Alert>
                                                                            )
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    </td>
                                );
                            })}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default DispatchCancellationJobAssignments;
